import { memo, useCallback, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import styled, { css } from '@mint-lib/styled';
import type { Identifier } from 'dnd-core';

import { Box, Icon, IconButton, SelectionListItem } from '@myn/mui';

import { useDoubleClick } from '../../../../hooks/useDoubleClick.js';
import { Dimension } from '../../../../types/campaignBuilder.js';
import {
  DimensionsDragType,
  REQUIRED_COLUMNS,
} from '../../EditModal.constants.js';
import { DraggableDimension } from '../../EditModal.types.js';

export type Props = {
  index: number;
  dimension: Dimension;
  onDimensionMove: (dragIndex: number, dropIndex: number) => void;
  onDimensionAddition: (dimension: Dimension, dropIndex: number) => void;
  onDimensionRemove: (dimension: Dimension) => void;
  className?: string;
};

const getDropIndex = (options: {
  isOverBefore: boolean;
  dragIndex: number;
  hoverIndex: number;
}): number => {
  const { isOverBefore, dragIndex, hoverIndex } = options;

  if (dragIndex === hoverIndex) {
    return dragIndex;
  }

  if (dragIndex < hoverIndex) {
    if (isOverBefore) {
      return hoverIndex - 1;
    }

    return hoverIndex;
  }

  if (isOverBefore) {
    return hoverIndex;
  }

  return hoverIndex + 1;
};

const DimensionRow = ({
  dimension,
  onDimensionAddition,
  onDimensionRemove,
  onDimensionMove,
  index: hoverIndex,
  className,
}: Props) => {
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [isOverBefore, setIsOverBefore] = useState<boolean>(false);

  const isDraggable = dimension.value !== 'media';

  const [{ handlerId, isOver }, drop] = useDrop<
    DraggableDimension,
    void,
    { handlerId: Identifier | null; isOver: boolean }
  >({
    accept: [
      DimensionsDragType.DIMENSION_ROW,
      DimensionsDragType.GROUPED_DIMENSION,
    ],
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
        isOver: monitor.isOver(),
      };
    },
    hover(_, monitor) {
      if (!previewRef.current) {
        return;
      }

      const dragDistanceTop = monitor.getClientOffset()?.y ?? 0;
      const hoverDistanceTop = previewRef.current.getBoundingClientRect().top;
      const hoverHeight = previewRef.current.clientHeight;

      setIsOverBefore(dragDistanceTop - hoverDistanceTop < hoverHeight / 2);
    },

    canDrop: () => isDraggable,

    drop({
      type,
      dimension: droppableDimension,
      index: dragIndex,
    }: DraggableDimension) {
      if (!previewRef.current || !droppableDimension) {
        return;
      }

      if (type === DimensionsDragType.GROUPED_DIMENSION) {
        const dropIndex = isOverBefore ? hoverIndex : hoverIndex + 1;

        onDimensionAddition(droppableDimension, dropIndex);

        return;
      }

      const dropIndex = getDropIndex({ isOverBefore, dragIndex, hoverIndex });

      if (dragIndex === dropIndex) {
        return;
      }

      onDimensionMove(dragIndex, dropIndex);
    },
  });

  const [{ isDragging }, drag, preview] = useDrag<
    DraggableDimension,
    void,
    { isDragging: boolean }
  >({
    type: DimensionsDragType.DIMENSION_ROW,
    item: {
      index: hoverIndex,
      type: DimensionsDragType.DIMENSION_ROW,
      dimension,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: () => isDraggable,
  });

  const handleRemove = useCallback(() => {
    if (REQUIRED_COLUMNS.includes(dimension.value)) {
      return;
    }
    onDimensionRemove(dimension);
  }, [onDimensionRemove, dimension]);

  drag(drop(preview(previewRef)));

  useDoubleClick({
    onDoubleClick: handleRemove,
    ref: previewRef,
  });

  return (
    <Box ref={previewRef}>
      <ColumnItem
        className={className}
        isDragging={isDragging}
        isDraggable={isDraggable}
        isOver={isOver}
        isOverBefore={isOverBefore}
        data-handler-id={handlerId}
        data-testid="dimension-row-wrapper"
        startAdornment={isDraggable && <Icon component="Draggable" />}
        text={dimension.name}
        endAdornment={
          !REQUIRED_COLUMNS.includes(dimension.value) && (
            <IconButton
              variant="icon"
              icon="Close"
              size="small"
              onClick={handleRemove}
            />
          )
        }
      />
    </Box>
  );
};

const ColumnItem = styled(SelectionListItem)<{
  isDragging?: boolean;
  isOver?: boolean;
  isOverBefore?: boolean;
  isDraggable?: boolean;
}>`
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  opacity: ${({ isDragging }) => (isDragging ? '0' : '1')};

  ${({ isOverBefore, isOver, isDraggable, theme }) => {
    const topValue = isOverBefore ? '-3px' : 'calc(100% + 1px)';

    return isOver && isDraggable
      ? css`
          &::after {
            position: absolute;
            content: '';
            top: ${topValue};
            left: 0;
            right: 0;
            height: 2px;
            background-color: ${theme.palette.primary.main};
          }
        `
      : null;
  }}
`;

export default memo(DimensionRow);
